import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { ChevronDown } from 'lucide-react';
import { useStoreInfo } from '@/lib/store';
import { motion, AnimatePresence } from 'framer-motion';

interface FAQItem {
  question: string;
  answer: string | string[];
}

const faqs: FAQItem[] = [
  {
    question: 'HOW LONG DOES DELIVERY TAKE?',
    answer: [
      'Standard shipping typically takes 3–7 business days.',
      'We partner with trusted and reliable couriers. Please note that delivery times are estimates and may be affected by unexpected delays.'
    ]
  },
  {
    question: 'WHAT IS YOUR RETURN POLICY?',
    answer: [
      'You have 30 days from the date you receive your order to return your item for any reason.',
      'Returns must be sent back within this window and items must be in unworn, unwashed condition, with original tags attached and suitable for resale.',
      'Please note: Shipping fees are non-refundable.'
    ]
  },
  {
    question: 'WHAT PAYMENT METHODS DO YOU ACCEPT?',
    answer: [
      'We only accept payments through Stripe and the methods supported by Stripe, including:',
      'Visa',
      'Mastercard',
      'American Express',
      'Apple Pay',
      'Google Pay',
      'Shop Pay',
      'Debit and credit cards'
    ]
  },
  {
    question: 'WHERE IS MY TRACKING NUMBER?',
    answer: "As soon as your order ships, you'll receive a confirmation email with your tracking number and a link to track your shipment."
  },
  {
    question: 'WHEN WILL MY CARD BE CHARGED?',
    answer: 'Your card will be charged immediately after your order is successfully placed.'
  }
];

export function FAQPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const { storeInfo } = useStoreInfo();
  const pageTitle = `FAQ${storeInfo?.site_name ? ` - ${storeInfo.site_name}` : ''}`;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content="Frequently Asked Questions about our products and services" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        {/* Hero Section */}
        <div className="relative bg-brand-primary py-24">
          <div className="absolute inset-0">
            <img 
              src={`https://marivieint.com/${storeInfo?.heroSlider?.[2] || ''}`}
              alt="FAQ background"
              className="w-full h-full object-cover opacity-20"
            />
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 font-display">
              Frequently Asked Questions
            </h1>
            <p className="text-xl text-white/90 max-w-2xl mx-auto">
              Find answers to common questions about our services
            </p>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden"
              >
                <button
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                  className="w-full px-6 py-4 text-left flex justify-between items-center"
                >
                  <span className="text-lg font-medium text-gray-900 dark:text-white">
                    {faq.question}
                  </span>
                  <motion.div
                    animate={{ rotate: openIndex === index ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ChevronDown className="h-5 w-5 text-gray-500" />
                  </motion.div>
                </button>

                <AnimatePresence>
                  {openIndex === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className="px-6 pb-4">
                        {Array.isArray(faq.answer) ? (
                          <div className="space-y-2">
                            {faq.answer.map((line, i) => (
                              <p
                                key={i}
                                className="text-gray-600 dark:text-gray-300"
                              >
                                {line}
                              </p>
                            ))}
                          </div>
                        ) : (
                          <p className="text-gray-600 dark:text-gray-300">
                            {faq.answer}
                          </p>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}